import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import AppleSignin from 'react-apple-signin-auth';
import { useGoogleLogin } from '@react-oauth/google';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Flexlogo from '../../assets/images/logo-red.png';

import * as AuthService from '../../api/authService';

import useAuthContext from '../../hooks/useAuthContext';
import useInitContext from '../../hooks/useInitContext';
import useNavContext from '../../hooks/useNavContext';

import useDeepLinkContext from '../../hooks/useDeepLinkContext';
import GoogleButton from './GoogleButton';
import AppleButton from './AppleButton';
import EmailButton from './EmailButton';
import MobileButton from './MobileButton';

const useStyles = makeStyles((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '90dvh', // Fill the viewport height
    [theme.breakpoints.up('sm')]: {
      width: 400,
      margin: '0 auto',
      marginTop: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      margin: '0 auto',
      marginTop: theme.spacing(3),
    },
  },
  form: {
    marginTop: theme.spacing(4),
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  grow: {
    flexGrow: 1,
  },
  socialAction: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(),
  },
  actionButton: {
    width: 150,
    color: '#666666',
  },
  disclaimer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    marginTop: 'auto', // Push to bottom
    paddingTop: theme.spacing(2),
  },
  logoContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(5),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    width: 140,
    height: 50,
  },
  merchantLogo: {
    height: 100,
    width: 100,
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 2px 10px rgba(199, 199, 199, 0.5)',
  },
  facebookButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: '#3578e5',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#365899',
      color: '#fff',
    },
  },
  caption: {
    lineHeight: '12px',
    color: theme.palette.common.black,
  },
  divider: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  checkboxNoMargin: {
    padding: 0,
    marginTop: 0,
    marginBottom: 0,
    marginRight: theme.spacing(),
  },
  middleText: {
    fontWeight: 'bold',
    fontSize: '14px',
    color: theme.palette.common.black,
  },
  switchContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(),
  },
}));

/**
 * Root redirects to the user's companies dashboard.
 */
export default function MainSplash() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const params = queryString.parse(location.search);
  const feature = params['feature'];
  const merchantId = params['merchant'];
  const contactlessCode = params['code'];
  const couponCode = params['code'];
  const merchantCode = params['merchant'];
  const userCode = params['user'];
  const singleUseCode = params['code'];
  const store = params['store'];
  const stampNum = params['numOfStamps'];
  const logo = params['logo'] as string;
  const merchantName = params['name'];
  const [isLoading, setIsLoading] = useState(true);
  const [agreement, setAgreement] = useState(true);

  const dl = useDeepLinkContext();
  const auth = useAuthContext();
  const nav = useNavContext();
  const init = useInitContext();

  const googleWebClient = process.env.REACT_APP_GOOGLE_OAUTH_WEB_CLIENT;
  const appleClient = process.env.REACT_APP_APPLE_CLIENT;
  const appleRedirectURI = process.env.REACT_APP_APPLE_REDIRECT_URI;
  const [isLogin, setIsLogin] = useState(false);
  const loginArray = new URLSearchParams(window.location.search).getAll('auth');

  useEffect(() => {
    const setDeeplink = () => {
      if (dl && !dl.deeplink) {
        dl.setDeeplink({
          feature: feature && typeof feature == 'string' ? feature : undefined,
          merchantId:
            merchantId && typeof merchantId == 'string'
              ? merchantId
              : undefined,
          contactlessCode:
            contactlessCode && typeof contactlessCode == 'string'
              ? contactlessCode
              : undefined,
          couponCode:
            couponCode && typeof couponCode == 'string'
              ? couponCode
              : undefined,
          merchantCode:
            merchantCode && typeof merchantCode == 'string'
              ? merchantCode
              : undefined,
          userCode:
            userCode && typeof userCode == 'string' ? userCode : undefined,
          logo: logo && typeof logo == 'string' ? logo : undefined,
          singleUseCode:
            singleUseCode && typeof singleUseCode == 'string'
              ? singleUseCode
              : undefined,
          store: store && typeof store == 'string' ? store : undefined,
          stampNum:
            stampNum && typeof stampNum == 'string' ? stampNum : undefined,
        });
      }
      setIsLoading(false);
    };

    if (isLoading) {
      setDeeplink();
    }
  }, [
    isLoading,
    feature,
    merchantId,
    contactlessCode,
    couponCode,
    merchantCode,
    userCode,
    dl,
    logo,
    singleUseCode,
    store,
    stampNum,
  ]);

  const deepLinkHandler = () => {
    if (dl) {
      dl.setDeeplink({
        feature: feature && typeof feature == 'string' ? feature : undefined,
        merchantId:
          merchantId && typeof merchantId == 'string' ? merchantId : undefined,
        contactlessCode:
          contactlessCode && typeof contactlessCode == 'string'
            ? contactlessCode
            : undefined,
        couponCode:
          couponCode && typeof couponCode == 'string' ? couponCode : undefined,
        merchantCode:
          merchantCode && typeof merchantCode == 'string'
            ? merchantCode
            : undefined,
        userCode:
          userCode && typeof userCode == 'string' ? userCode : undefined,
        logo: logo && typeof logo == 'string' ? logo : undefined,
      });
    }
  };

  const handleGoogleLogin = async (googleToken: string) => {
    try {
      const res: any = await AuthService.googleLogin(googleToken);
      console.log('res: ', res);
      const { token } = res.data.attributes;

      auth?.setSession({
        id: res.data.id,
        token: token,
        userId: '1',
      });
      if (feature) {
        deepLinkHandler();
        history.go(0);
      } else {
        nav?.setNav({
          menuNav: 'home',
        });
        init?.setInit({
          isFirstLoginSignup: true,
        });
        history.push('/');
        history.go(0);
      }
    } catch (e: any) {
      console.log('error: ', e);
    }
  };

  const handleAppleLogin = async (
    appleToken: string,
    firstName: string,
    lastName: string
  ) => {
    try {
      const res: any = await AuthService.appleWebLogin(
        appleToken,
        firstName,
        lastName
      );

      console.log('res: ', res);
      const { token } = res.data.attributes;

      auth?.setSession({
        id: res.data.id,
        token: token,
        userId: '1',
      });

      if (feature) {
        deepLinkHandler();
        history.go(0);
      } else {
        nav?.setNav({
          menuNav: 'home',
        });
        init?.setInit({
          isFirstLoginSignup: true,
        });
        history.push('/');
        history.go(0);
      }
    } catch (e: any) {
      console.log('error: ', e);
    }
  };

  const responseGoogle = useGoogleLogin({
    onSuccess: (codeResponse) => {
      handleGoogleLogin(codeResponse.access_token);
    },
  });

  const responseApple = (response: any) => {
    if (response && response.authorization && response.authorization.code) {
      const code = response.authorization.code;
      const firstName =
        response.user && response.user.name && response.user.name.firstName
          ? response.user.name.firstName
          : '';
      const lastName =
        response.user && response.user.name && response.user.name.lasstName
          ? response.user.name.lastName
          : '';
      handleAppleLogin(code, firstName, lastName);
    }
    console.log(response);
  };

  return (
    <div>
      <Grid container>
        <Grid item xs={12}>
          <div className={classes.content}>
            <div className={classes.logoContainer}>
              <img
                src={logo ? logo : Flexlogo}
                className={logo ? classes.merchantLogo : classes.logo}
                alt="flex reward"
              />
            </div>
            <div className={classes.socialAction}>
              {(loginArray.length === 0 ||
                loginArray.includes('all') ||
                loginArray.includes('apple')) &&
                appleRedirectURI && (
                  <AppleSignin
                    authOptions={{
                      clientId: appleClient,
                      scope: 'email name',
                      redirectURI: appleRedirectURI,
                      state: 'origin:web',
                      nonce: ' nonce',
                      usePopup: true,
                    }}
                    onSuccess={responseApple}
                    render={(props: any) => (
                      <AppleButton
                        onClick={props.onClick}
                        disabled={!agreement}
                        isLogin={isLogin}
                      />
                    )}
                  />
                )}
              {(loginArray.length === 0 ||
                loginArray.includes('all') ||
                loginArray.includes('google')) &&
                googleWebClient && (
                  <GoogleButton
                    onClick={() => responseGoogle()}
                    disabled={!agreement}
                    isLogin={isLogin}
                  />
                )}
              {(loginArray.length === 0 ||
                loginArray.includes('all') ||
                loginArray.includes('email')) && (
                <EmailButton
                  onClick={() => {
                    isLogin
                      ? history.push('/loginEmail')
                      : history.push('/signupEmail');
                  }}
                  disabled={!agreement}
                  isLogin={isLogin}
                />
              )}
              {(loginArray.length === 0 ||
                loginArray.includes('all') ||
                loginArray.includes('mobile')) && (
                <MobileButton
                  onClick={() => {
                    isLogin
                      ? history.push('/loginMobile')
                      : history.push('/signupMobile');
                  }}
                  disabled={!agreement}
                  isLogin={isLogin}
                />
              )}
              <div className={classes.divider}>
                <Divider style={{ width: '25%', margin: '24px 0' }} />
                <Typography variant="caption" className={classes.middleText}>
                  {isLogin
                    ? "Don't Have An Account?"
                    : 'Already Have An Account?'}
                </Typography>
                <Divider style={{ width: '25%', margin: '24px 0' }} />
              </div>
              <div className={classes.switchContainer}>
                <Typography variant="caption">SWITCH TO</Typography>
              </div>
            </div>
            <div className={classes.actions}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => setIsLogin(!isLogin)}
                className={classes.actionButton}
                disabled={!agreement}
              >
                {isLogin ? 'SIGN UP' : 'LOG IN'}
              </Button>
            </div>
            <div className={classes.disclaimer}>
              <Checkbox
                size="small"
                checked={agreement}
                onChange={(e) => setAgreement(e.target.checked)}
                color="default"
                className={classes.checkboxNoMargin}
              />
              <div>
                <Typography variant="caption" className={classes.caption}>
                  I agree to Flex Rewards'&nbsp;
                </Typography>
                <Typography variant="caption" className={classes.caption}>
                  <Link
                    href={'https://myflexrewards.com/terms/'}
                    target="blank"
                  >
                    Terms of Service
                  </Link>
                </Typography>

                <Typography variant="caption" className={classes.caption}>
                  &nbsp;and&nbsp;
                </Typography>
                <Typography variant="caption" className={classes.caption}>
                  <Link
                    href={'https://myflexrewards.com/privacy/'}
                    target="blank"
                  >
                    Privacy Policy
                  </Link>
                </Typography>
                <Typography variant="caption" className={classes.caption}>
                  , and I give explicit permission for Flex Rewards to share my
                  personal information with{' '}
                  {merchantName ? merchantName : 'participating merchants'} and
                  for Flex Rewards and{' '}
                  {merchantName ? merchantName : 'participating merchants'} to
                  send me marketing communications, including, but not limited
                  to, emails, push notifications, and in-app messages.
                </Typography>
              </div>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
}
